// src/components/FilterBar.jsx
import React from "react";
import "./styling/filterBar.css";

function Collapsible({ title, children, defaultOpen = false }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  const bodyRef = React.useRef(null);
  const anim = React.useRef(false);
  const regionId = React.useId();
  const labelId = React.useId();
  const D = 220; // ms

  const toggle = React.useCallback(() => {
    if (anim.current) return;
    const el = bodyRef.current;
    if (!el) { setOpen(o => !o); return; }

    anim.current = true;
    el.style.overflow = "hidden";
    el.style.willChange = "height, opacity";

    const cleanup = () => {
      el.style.transition = "";
      el.style.willChange = "";
      anim.current = false;
    };

    if (open) {
      // SLUITEN
      const start = el.scrollHeight;
      el.style.height = start + "px";
      el.style.opacity = "1";
      // force reflow
      void el.offsetHeight;
      el.style.transition = `height ${D}ms ease, opacity 180ms ease`;
      el.style.height = "0px";
      el.style.opacity = "0";
      const onEnd = (ev) => {
        if (ev.propertyName !== "height") return;
        setOpen(false);
        el.removeEventListener("transitionend", onEnd);
        cleanup();
        // laat height leeg zodat CSS-basis geldt (height:0), body is verborgen
      };
      el.addEventListener("transitionend", onEnd);
    } else {
      // OPENEN
      setOpen(true); // zodat content beschikbaar/meetbaar is
      requestAnimationFrame(() => {
        const target = bodyRef.current?.scrollHeight ?? 0;
        el.style.height = "0px";
        el.style.opacity = "0";
        // force reflow
        void el.offsetHeight;
        el.style.transition = `height ${D}ms ease, opacity 180ms ease`;
        el.style.height = target + "px";
        el.style.opacity = "1";
        const onEnd = (ev) => {
          if (ev.propertyName !== "height") return;
          // laat de sectie flexibel meegroeien
          el.style.height = "auto";
          el.removeEventListener("transitionend", onEnd);
          cleanup();
        };
        el.addEventListener("transitionend", onEnd);
      });
    }
  }, [open]);

  return (
    <div className={`facetGroup ${open ? "is-open" : ""}`}>
      <button
        type="button"
        className="facetSummary"
        aria-expanded={open}
        aria-controls={regionId}
        id={labelId}
        onClick={toggle}
      >
        <span>{title}</span>
        {/* Bootstrap Icons */}
        <i className="bi bi-plus-lg icon-plus" aria-hidden="true"></i>
        <i className="bi bi-dash-lg icon-minus" aria-hidden="true"></i>
      </button>

      <div
        id={regionId}
        ref={bodyRef}
        className="collapsibleBody"
        role="region"
        aria-labelledby={labelId}
      >
        <div className="collapsibleInner">{children}</div>
      </div>
    </div>
  );
}

function FacetGroup({ title, options = [], selected = [], onToggle, defaultOpen = false }) {
  return (
    <Collapsible title={title} defaultOpen={defaultOpen}>
      <div className="facetGrid">
        {(options || []).map((opt) => (
          <label key={opt} className="facetItem cb">
            <input
                type="checkbox"
                checked={(selected || []).includes(opt)}
                onChange={() => onToggle(opt)}
            />
            <span className="cb-box" aria-hidden="true">
                <i className="bi bi-check-lg"></i>
            </span>
            <span className="cb-label">{opt}</span>
            </label>
        ))}
      </div>
    </Collapsible>
  );
}

// function FacetGroup({ title, options = [], selected = [], onToggle }) {
//   return (
//     <div className="facetGroup">
//       <h4>{title}</h4>
//       <div className="facetGrid">
//         {(options || []).map((opt) => (
//           <label key={opt} className="facetItem">
//             <input
//               type="checkbox"
//               checked={(selected || []).includes(opt)}
//               onChange={() => onToggle(opt)}
//             />
//             <span>{opt}</span>
//           </label>
//         ))}
//       </div>
//     </div>
//   );
// }

export default function FilterBar({
  filters,
  setFilters,
  priceBounds,
  inhoudBounds,
  facets = {},
}) {
  const {
    productTypes = [],
    countries = [],
    streken = [],
    wijnhuizen = [],
    regios = [],
    grapes = [],
    wijnsoorten = [],
  } = facets;

  const setVal = (key, val) => setFilters((prev) => ({ ...prev, [key]: val }));

  const toggleFromArray = (key, val) =>
    setFilters((prev) => {
      const arr = prev[key] || [];
      const next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
      return { ...prev, [key]: next };
    });

  return (
    <div className="filterBar">

        <h3>Verfijnen op</h3>

         {/* Reset-knop (optioneel) */}
      <div className="filterActions">
        <button
          type="button"
          onClick={() =>
            setFilters((f) => ({
              ...f,
              q: "",
              inStockOnly: false,
              productTypes: [],
              countries: [],
              streken: [],
              wijnhuizen: [],
              regios: [],
              grapes: [],
              wijnsoorten: [],
              // laat prijs/inhoud staan of reset ze ook, jouw keuze:
              // minPrice: priceBounds?.min ?? 0,
              // maxPrice: priceBounds?.max ?? 9999,
              // minInhoud: inhoudBounds?.min ?? 0,
              // maxInhoud: inhoudBounds?.max ?? 3,
            }))
          }
        >
          Reset filters
        </button>
      </div>

      {/* Zoeken */}
      <div className="nameFilter">
        <label>Naam</label>
        <input
          type="search"
          placeholder="Zoek product…"
          value={filters.q}
          onChange={(e) => setVal("q", e.target.value)}
        />
      </div>

      {/* Prijs-range (€) */}
      <div className="priceFilter">
        <label>Prijs</label>
        <div className="priceRange">
          <span>€</span>
          <input
            type="number"
            min={priceBounds?.min ?? 0}
            max={priceBounds?.max ?? 9999}
            value={filters.minPrice}
            onChange={(e) => setVal("minPrice", Number(e.target.value))}
          />
          <span>—</span>
          <input
            type="number"
            min={priceBounds?.min ?? 0}
            max={priceBounds?.max ?? 9999}
            value={filters.maxPrice}
            onChange={(e) => setVal("maxPrice", Number(e.target.value))}
          />
        </div>
      </div>

     

      {/* Facetten (multi-select via checkboxes) */}
      <FacetGroup
        title="Type"
        options={productTypes}
        selected={filters.productTypes}
        onToggle={(v) => toggleFromArray("productTypes", v)}
      />
      <FacetGroup
        title="Wijnsoort"
        options={wijnsoorten}
        selected={filters.wijnsoorten}
        onToggle={(v) => toggleFromArray("wijnsoorten", v)}
      />

      <Collapsible title="Kenmerken">
        <label className="facetItem cb">
          <input
            type="checkbox"
            checked={!!filters.mousserend}
            onChange={(e) => setFilters(f => ({ ...f, mousserend: e.target.checked }))}
          />
          <span className="cb-box" aria-hidden="true">
            <i className="bi bi-check-lg"></i>
          </span>
          <span className="cb-label">Mousserend</span>
        </label>

        <label className="facetItem cb">
          <input
            type="checkbox"
            checked={!!filters.natuurwijn}
            onChange={(e) => setFilters(f => ({ ...f, natuurwijn: e.target.checked }))}
          />
          <span className="cb-box" aria-hidden="true">
            <i className="bi bi-check-lg"></i>
          </span>
          <span className="cb-label">Natuurwijn</span>
        </label>
      </Collapsible>

      <FacetGroup
        title="Druif"
        options={grapes}
        selected={filters.grapes}
        onToggle={(v) => toggleFromArray("grapes", v)}
      />

      <FacetGroup
        title="Land"
        options={countries}
        selected={filters.countries}
        onToggle={(v) => toggleFromArray("countries", v)}
      />

       <FacetGroup
        title="Regio"
        options={regios}
        selected={filters.regios}
        onToggle={(v) => toggleFromArray("regios", v)}
      />

      <FacetGroup
        title="Streek"
        options={streken}
        selected={filters.streken}
        onToggle={(v) => toggleFromArray("streken", v)}
      />

      <FacetGroup
        title="Wijnhuis"
        options={wijnhuizen}
        selected={filters.wijnhuizen}
        onToggle={(v) => toggleFromArray("wijnhuizen", v)}
      /> 

       {/* Inhoud-range (L) */}
      <Collapsible title={"Inhoud"} defaultOpen={false}>
      <div className="volumeFilter">
        <label></label>
        <div className="priceRange">
          
          <input
            type="number"
            step="0.1"
            min={inhoudBounds?.min ?? 0}
            max={inhoudBounds?.max ?? 3}
            value={filters.minInhoud}
            onChange={(e) => setVal("minInhoud", Number(e.target.value))}
          />
          <span>—</span>
          <input
            type="number"
            step="0.1"
            min={inhoudBounds?.min ?? 0}
            max={inhoudBounds?.max ?? 3}
            value={filters.maxInhoud}
            onChange={(e) => setVal("maxInhoud", Number(e.target.value))}
          />          
        <span>Liter</span>
        </div>
      </div>
      </Collapsible>

     
    </div>
  );
}
